// src/screens/DevGalleryScreen.js
// ============================================================================
// DEV-ONLY SCREENSHOT GALLERY
//
// Renders every match-lifecycle modal (MatchReadyContext) with mock data so
// marketing/how-to screenshots can be captured without orchestrating a live
// event. Only available on localhost / dev builds — production visitors get
// nothing. Not linked from any nav.
//
//   /dev/gallery                → scenario picker
//   /dev/gallery?scenario=<key> → renders just that modal (headless-capture
//                                 friendly: one URL per screenshot)
// ============================================================================

import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Platform } from 'react-native';
import { MatchReadyProvider, PHASE } from '../context/MatchReadyContext';
import CeremonyScenarioHost, { CEREMONY_SCENARIOS } from './DevCeremonyGallery';

const isDevHost = () =>
  (typeof __DEV__ !== 'undefined' && __DEV__) ||
  (Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname));

const YOU = 'demo-you';
const OPP = 'demo-opp';

const mins = (n) => new Date(Date.now() + n * 60 * 1000);

const buildScenarios = () => {
  const baseMatch = {
    id: 'demo-match',
    path: 'tournaments/demo-event/matches/demo-match',
    status: 'locked',
    eventId: 'demo-event',
    readyDeadline: mins(4),
  };

  const h2h = (youReady, oppReady) => ({
    playersReadyState: {
      [YOU]: { ready: youReady, displayName: 'CritSeeker' },
      [OPP]: { ready: oppReady, displayName: 'NightShade77' },
    },
    playersReady: (youReady ? 1 : 0) + (oppReady ? 1 : 0),
    totalPlayersRequired: 2,
  });

  // Full-grid FFA roster (GT7 showdown shape): 16 racers, real IGNs from the
  // demo grid. Exercises the two-up ready list — the single-column layout only
  // ever showed a handful.
  const GRID_IGNS = [
    'RaceR xXx', 'Milioli', 'Gooloo', 'CritCash',
    'Jesusjulie', 'Fabbres', 'MM Racing Guy', 'ADONAIGT',
    'Mateoracing', 'C1ean Racer', 'Senninha', 'Garewal',
    'Cpalm', 'Danez_echo1', 'Romero', 'GTBBR',
  ];

  const fullGrid = (readyCount) => {
    const playersReadyState = {};
    GRID_IGNS.forEach((ign, i) => {
      const uid = i === 3 ? YOU : `demo-p${i + 1}`; // CritCash = you
      playersReadyState[uid] = { ready: i < readyCount, displayName: ign };
    });
    return {
      playersReadyState,
      playersReady: readyCount,
      totalPlayersRequired: GRID_IGNS.length,
    };
  };

  const sf6 = {
    gameName: 'Street Fighter 6',
    lobbyName: 'CRIT-4207',
    lobbyPassword: '9183',
  };

  return {
    ready_up_full_grid: {
      title: 'Ready Up — 16-racer full grid (GT7)',
      mock: {
        phase: PHASE.READY_UP,
        user: { uid: YOU },
        pendingMatch: {
          ...baseMatch,
          gameName: 'Gran Turismo 7',
          lobbyName: 'CRIT-SHOWDOWN',
          ...fullGrid(11),
        },
      },
    },
    ready_up: {
      title: 'Ready Up — lobby game',
      mock: {
        phase: PHASE.READY_UP,
        user: { uid: YOU },
        pendingMatch: { ...baseMatch, ...sf6, ...h2h(false, true) },
      },
    },
    ready_up_madden: {
      title: 'Ready Up — Madden (EA invite)',
      mock: {
        phase: PHASE.READY_UP,
        user: { uid: YOU },
        pendingMatch: {
          ...baseMatch,
          gameName: 'Madden NFL 26',
          inviteMode: 'ea_ign',
          inviterUserId: YOU,
          playersReadyState: {
            [YOU]: { ready: false, displayName: 'CritSeeker', ign: 'CritSeeker99' },
            [OPP]: { ready: true, displayName: 'NightShade77', ign: 'GridironGhost' },
          },
          playersReady: 1,
          totalPlayersRequired: 2,
        },
      },
    },
    ready_up_gt7: {
      title: 'Ready Up — GT7 (Room ID)',
      mock: {
        phase: PHASE.READY_UP,
        user: { uid: YOU },
        pendingMatch: {
          ...baseMatch,
          game: 'gt7',
          gameName: 'Gran Turismo 7',
          roomId: 'K7M2QX',
          playersReadyState: {
            [YOU]: { ready: false, displayName: 'CritSeeker' },
            'demo-r2': { ready: true, displayName: 'ApexWolf' },
            'demo-r3': { ready: true, displayName: 'RedlineRita' },
            'demo-r4': { ready: false, displayName: 'TurboNomad' },
            'demo-r5': { ready: true, displayName: 'PitLaneKing' },
            'demo-r6': { ready: false, displayName: 'SlipstreamSam' },
          },
          playersReady: 3,
          totalPlayersRequired: 6,
        },
      },
    },
    waiting: {
      title: 'Waiting for Opponent',
      mock: {
        phase: PHASE.WAITING_OTHERS,
        user: { uid: YOU },
        pendingMatch: { ...baseMatch, ...sf6, ...h2h(true, false) },
      },
    },
    all_ready: {
      title: 'All Players Ready',
      mock: {
        phase: PHASE.ALL_READY,
        user: { uid: YOU },
        pendingMatch: { ...baseMatch, ...sf6, ...h2h(true, true) },
      },
    },
    verifying: {
      title: 'Verifying (stream scan)',
      mock: {
        phase: PHASE.VERIFYING,
        user: { uid: YOU },
        pendingMatch: { ...baseMatch, ...sf6, ...h2h(true, true), readyStatus: 'verifying' },
      },
    },
    verification_failed: {
      title: 'Verification Failed (re-run)',
      mock: {
        phase: PHASE.VERIFICATION_FAILED,
        user: { uid: YOU },
        pendingMatch: {
          ...baseMatch, ...sf6, ...h2h(true, true),
          readyStatus: 'verification_failed',
          playerVerifyRetries: 0,
        },
      },
    },
    crit_time: {
      title: 'CRIT TIME! (match live)',
      mock: {
        phase: PHASE.CRIT_TIME,
        user: { uid: YOU },
        pendingMatch: { ...baseMatch, ...sf6, ...h2h(true, true), status: 'live' },
      },
    },
    result_win: {
      title: 'Victory (next round + host rating)',
      mock: {
        phase: PHASE.RESULT_WIN,
        user: { uid: YOU },
        matchResult: {
          matchId: 'demo-match',
          matchPath: baseMatch.path,
          outcome: 'win',
          isWinner: true,
          opponentName: 'NightShade77',
          roundName: 'Round 2',
          gameName: 'Street Fighter 6',
          gameId: 'sf6',
          eventName: 'Friday Night Crits',
          eventId: 'demo-event',
          hostUid: 'demo-host',
          nextMatchPath: 'tournaments/demo-event/matches/demo-next',
        },
      },
    },
    result_loss: {
      title: 'Match Complete — loss + dispute window',
      mock: {
        phase: PHASE.RESULT_LOSS,
        user: { uid: YOU },
        matchResult: {
          matchId: 'demo-match',
          matchPath: baseMatch.path,
          outcome: 'loss',
          isWinner: false,
          opponentName: 'NightShade77',
          roundName: 'Round 2',
          gameName: 'Street Fighter 6',
          eventId: 'demo-event',
          disputeWindowEndsAt: mins(118),
        },
      },
    },
    under_review: {
      title: 'Under Review — file a claim',
      mock: {
        phase: PHASE.UNDER_REVIEW,
        user: { uid: YOU },
        pendingMatch: {
          ...baseMatch, ...sf6, ...h2h(true, true),
          status: 'live',
          visionRequiresReview: true,
        },
      },
    },
    champion: {
      title: 'Champion (tournament winner)',
      mock: {
        phase: PHASE.CHAMPION,
        user: { uid: YOU },
        matchResult: {
          matchId: 'demo-match',
          matchPath: baseMatch.path,
          outcome: 'win',
          isWinner: true,
          isFinalMatch: true,
          opponentName: 'NightShade77',
          eventName: 'Friday Night Crits',
          eventId: 'demo-event',
          prizeAmount: 250,
        },
      },
    },
    next_round: {
      title: 'Advancing (next round)',
      mock: {
        phase: PHASE.NEXT_ROUND,
        user: { uid: YOU },
        matchResult: {
          matchId: 'demo-match',
          matchPath: baseMatch.path,
          outcome: 'win',
          isWinner: true,
          opponentName: 'NightShade77',
          eventId: 'demo-event',
        },
      },
    },
  };
};

export default function DevGalleryScreen({ route, navigation }) {
  const scenarios = useMemo(buildScenarios, [route?.params?.scenario]);

  if (!isDevHost()) {
    return (
      <View style={styles.page}>
        <Text style={styles.dim}>Not available.</Text>
      </View>
    );
  }

  const key = route?.params?.scenario;
  const capturingNow = String(route?.params?.capture || '') === '1';

  // Ceremony surfaces are their own tree (not MatchReadyProvider modals), so
  // they get their own host.
  if (key && CEREMONY_SCENARIOS[key]) {
    return (
      <View style={styles.page}>
        <CeremonyScenarioHost key={key} scenarioKey={key} />
        {!capturingNow && (
          <TouchableOpacity
            style={styles.backLink}
            onPress={() => navigation.navigate('DevGallery', { scenario: undefined })}
          >
            <Text style={styles.dim}>{'←'} gallery</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  const active = key && scenarios[key];

  if (active) {
    // capture=1 (headless screenshot runs) hides the back link so nothing
    // bleeds through the modal overlay.
    const capturing = String(route?.params?.capture || '') === '1';
    return (
      <MatchReadyProvider key={key} mockState={active.mock}>
        <View style={styles.page}>
          {!capturing && (
            <TouchableOpacity
              style={styles.backLink}
              onPress={() => navigation.navigate('DevGallery', { scenario: undefined })}
            >
              <Text style={styles.dim}>← gallery</Text>
            </TouchableOpacity>
          )}
        </View>
      </MatchReadyProvider>
    );
  }

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.list}>
      <Text style={styles.heading}>Match-flow screenshot gallery</Text>
      <Text style={styles.dim}>Dev only — renders each modal with mock data.</Text>

      <Text style={styles.section}>Event ceremony (EventWatch b-roll)</Text>
      {Object.entries(CEREMONY_SCENARIOS).map(([slug, s]) => (
        <TouchableOpacity
          key={slug}
          style={styles.item}
          onPress={() => navigation.navigate('DevGallery', { scenario: slug })}
        >
          <Text style={styles.itemText}>{s.title}</Text>
          <Text style={styles.slug}>?scenario={slug}</Text>
        </TouchableOpacity>
      ))}

      <Text style={styles.section}>Match-flow modals</Text>
      {Object.entries(scenarios).map(([slug, s]) => (
        <TouchableOpacity
          key={slug}
          style={styles.item}
          onPress={() => navigation.navigate('DevGallery', { scenario: slug })}
        >
          <Text style={styles.itemText}>{s.title}</Text>
          <Text style={styles.slug}>?scenario={slug}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: '#0d0d0e' },
  list: { padding: 24, maxWidth: 560, width: '100%', alignSelf: 'center' },
  heading: { color: '#FFD700', fontSize: 22, fontWeight: 'bold', marginBottom: 4 },
  section: {
    color: '#FCD000', fontSize: 13, fontWeight: '900', letterSpacing: 2,
    marginTop: 18, marginBottom: 8, textTransform: 'uppercase',
  },
  dim: { color: '#888', fontSize: 13, marginBottom: 12 },
  item: {
    backgroundColor: '#1a1a1a', borderRadius: 10, borderWidth: 1, borderColor: '#333',
    padding: 14, marginBottom: 8, flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemText: { color: '#fff', fontSize: 15, fontWeight: '600' },
  slug: { color: '#666', fontSize: 12 },
  backLink: { position: 'absolute', top: 12, left: 16, padding: 8, zIndex: 1 },
});
